import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { Observable, map } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { ResponseApiInterface } from '../../../core/interfaces';
import {
  CatalogProductInterface,
  CatalogProductOptionInterface,
  CatalogProductPayloadInterface,
  CatalogProductQueryParamsInterface,
  CatalogUnitOfMeasureInterface
} from '../interfaces';

@Injectable({ providedIn: 'root' })
export class CatalogProductService {
  private readonly http = inject(HttpClient);
  private readonly url = `${environment.url}/api/v2/catalog/products`;
  private readonly unitsUrl = `${environment.url}/api/v2/catalog/units-of-measure`;

  list(
    params: CatalogProductQueryParamsInterface = {}
  ): Observable<ResponseApiInterface<CatalogProductInterface[]>> {
    return this.http.get<ResponseApiInterface<CatalogProductInterface[]>>(this.url, {
      params: this.toHttpParams(params)
    });
  }

  get(productId: string): Observable<CatalogProductInterface> {
    return this.http
      .get<ResponseApiInterface<CatalogProductInterface>>(`${this.url}/${productId}`)
      .pipe(map((response) => this.requireData(response)));
  }

  create(payload: CatalogProductPayloadInterface): Observable<CatalogProductInterface> {
    return this.http
      .post<ResponseApiInterface<CatalogProductInterface>>(this.url, payload)
      .pipe(map((response) => this.requireData(response)));
  }

  update(
    productId: string,
    payload: Partial<CatalogProductPayloadInterface>
  ): Observable<CatalogProductInterface> {
    const { prices, costs, barcodes, barcodeDeleteIds, variants, variantDeleteIds, priceDeleteIds, costDeleteIds, images, imageDeleteIds, taxes, ...product } = payload;
    const updatePayload = {
      ...product,
      priceOperations: prices || priceDeleteIds?.length ? { upsert: prices, deleteIds: priceDeleteIds } : undefined,
      costOperations: costs || costDeleteIds?.length ? { upsert: costs, deleteIds: costDeleteIds } : undefined,
      barcodeOperations:
        barcodes || barcodeDeleteIds
          ? { upsert: barcodes, deleteIds: barcodeDeleteIds }
          : undefined,
      variantOperations: variants || variantDeleteIds?.length ? { upsert: variants, deleteIds: variantDeleteIds } : undefined,
      imageOperations: images || imageDeleteIds?.length ? { upsert: images, deleteIds: imageDeleteIds } : undefined,
      taxOperations: taxes ? { upsert: taxes } : undefined
    };
    return this.http
      .patch<ResponseApiInterface<CatalogProductInterface>>(`${this.url}/${productId}`, updatePayload)
      .pipe(map((response) => this.requireData(response)));
  }

  delete(productId: string): Observable<void> {
    return this.http.delete<void>(`${this.url}/${productId}`);
  }

  search(query: string, limit = 20): Observable<CatalogProductOptionInterface[]> {
    return this.http
      .get<ResponseApiInterface<CatalogProductOptionInterface[]>>(`${this.url}/search`, {
        params: { query, limit }
      })
      .pipe(map((response) => response.data ?? []));
  }

  listUnits(): Observable<CatalogUnitOfMeasureInterface[]> {
    return this.http
      .get<ResponseApiInterface<CatalogUnitOfMeasureInterface[]>>(this.unitsUrl, {
        params: { status: 'active', page: 1, limit: 100, sortBy: 'name', sortDirection: 'ASC' }
      })
      .pipe(map((response) => response.data ?? []));
  }

  private requireData<T>(response: ResponseApiInterface<T>): T {
    if (!response.data) throw new Error('Catalog Product API returned no data');
    return response.data;
  }

  private toHttpParams(
    params: CatalogProductQueryParamsInterface
  ): Record<string, string | number> {
    return Object.entries(params).reduce<Record<string, string | number>>(
      (result, [key, value]) => {
        if (value !== undefined && value !== '') result[key] = value;
        return result;
      },
      {}
    );
  }
}
